export const INSERT_AFFILIATE_COMPANY_CODE = "Chickytutor";
export const INSERT_AFFILIATE_COMPANY_ID = "Lkcc8fFF2iWw3Bgt6ZBwSDcL2X12";
export const INSERT_AFFILIATE_UTM_SOURCE = "insertAffiliate";
export const INSERT_AFFILIATE_DASHBOARD_URL = "https://app.insertaffiliate.com/";

const INSERT_AFFILIATE_CAMPAIGN_PATTERN = /^[a-z0-9]{3,25}$/i;
const AFFILIATE_ATTRIBUTION_TIMEOUT_MS = 2_000;

export type InsertAffiliatePurchaseMetadata = {
  utm_source: typeof INSERT_AFFILIATE_UTM_SOURCE;
  utm_medium: typeof INSERT_AFFILIATE_COMPANY_ID;
  utm_campaign: string;
};

export const INSERT_AFFILIATE_SIGNUP_URL =
  "https://app.insertaffiliate.com/signup/affiliate?companyName=Chickytutor";

export const INSERT_AFFILIATE_SIGNUP_IFRAME_URL =
  "https://app.insertaffiliate.com/signup/affiliate-signup-iframe?companyName=Chickytutor&backgroundColor=%23ffffff&textColor=%23000000&buttonColor=%23f09b24";

export function normalizeInsertAffiliateEmail(value: unknown) {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  return normalized && normalized.length <= 320 ? normalized : null;
}

export function parseInsertAffiliateRevenueCatLink(value: unknown) {
  if (typeof value !== "string" || value.length > 2048) return null;

  try {
    const url = new URL(value);
    const campaign = url.searchParams.get("utm_campaign");
    const isExpectedLink =
      url.protocol === "https:" &&
      url.hostname === "pay.rev.cat" &&
      url.searchParams.get("utm_source") === INSERT_AFFILIATE_UTM_SOURCE &&
      url.searchParams.get("utm_medium") === INSERT_AFFILIATE_COMPANY_ID &&
      Boolean(campaign && INSERT_AFFILIATE_CAMPAIGN_PATTERN.test(campaign));

    return isExpectedLink ? url.toString() : null;
  } catch {
    return null;
  }
}

export function getInsertAffiliatePurchaseMetadata(
  providerDeepLink: unknown
): InsertAffiliatePurchaseMetadata | null {
  const parsedLink = parseInsertAffiliateRevenueCatLink(providerDeepLink);
  if (!parsedLink) return null;

  const campaign = new URL(parsedLink).searchParams.get("utm_campaign");
  if (!campaign || !INSERT_AFFILIATE_CAMPAIGN_PATTERN.test(campaign)) return null;

  return {
    utm_source: INSERT_AFFILIATE_UTM_SOURCE,
    utm_medium: INSERT_AFFILIATE_COMPANY_ID,
    utm_campaign: campaign,
  };
}

function parsePurchaseMetadataResponse(value: unknown): InsertAffiliatePurchaseMetadata | null {
  if (!value || typeof value !== "object") return null;
  const attribution = (value as { attribution?: unknown }).attribution;
  if (!attribution || typeof attribution !== "object") return null;

  const metadata = attribution as Record<string, unknown>;
  if (
    metadata.utm_source !== INSERT_AFFILIATE_UTM_SOURCE ||
    metadata.utm_medium !== INSERT_AFFILIATE_COMPANY_ID ||
    typeof metadata.utm_campaign !== "string" ||
    !INSERT_AFFILIATE_CAMPAIGN_PATTERN.test(metadata.utm_campaign)
  ) {
    return null;
  }

  return {
    utm_source: INSERT_AFFILIATE_UTM_SOURCE,
    utm_medium: INSERT_AFFILIATE_COMPANY_ID,
    utm_campaign: metadata.utm_campaign,
  };
}

export async function enrichRevenueCatMetadataWithInsertAffiliate(
  metadata: Record<string, string>,
  fetcher: typeof fetch = fetch
): Promise<Record<string, string>> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), AFFILIATE_ATTRIBUTION_TIMEOUT_MS);

  try {
    const response = await fetcher("/api/affiliate/attribution", {
      cache: "no-store",
      credentials: "same-origin",
      signal: controller.signal,
    });
    if (!response.ok) return metadata;

    const affiliateMetadata = parsePurchaseMetadataResponse(await response.json());
    if (!affiliateMetadata) return metadata;

    const enrichedMetadata = { ...metadata };
    for (const key of ["utm_source", "utm_medium", "utm_campaign"] as const) {
      const existingValue = enrichedMetadata[key];
      if (existingValue && existingValue !== affiliateMetadata[key]) {
        enrichedMetadata[`ct_${key}`] = existingValue;
      }
      enrichedMetadata[key] = affiliateMetadata[key];
    }
    return enrichedMetadata;
  } catch {
    return metadata;
  } finally {
    clearTimeout(timeout);
  }
}
